<template>
  <div class="late-fees-panel">
    <div class="late-fees-title">
      <span>Add Late Fees</span>
      <div class="title-actions">
        <button class="close-btn" @click="fetchLateFees" title="Refresh">
          <i class="fas fa-sync-alt"></i>
        </button>
        <button class="close-btn" @click="$emit('close')">
          <i class="fas fa-times"></i>
        </button>
      </div>
    </div>

    <div class="late-fees-body">
      <!-- Controls row -->
      <div class="controls-row">
        <div class="fee-input-group">
          <label>Late Fee Amount: $</label>
          <input
            type="number"
            v-model.number="feeAmount"
            min="0"
            step="25"
            class="fee-input"
          />
        </div>
        <div class="search-input-group">
          <i class="fas fa-search search-icon"></i>
          <input
            type="text"
            v-model="searchQuery"
            placeholder="Filter by address, tenant, or status..."
            class="search-input"
          />
          <button
            v-if="searchQuery"
            class="search-clear-btn"
            @click="searchQuery = ''"
          >
            <i class="fas fa-times"></i>
          </button>
        </div>
        <div class="control-buttons">
          <button class="btn btn-select-all" @click="selectAll">
            Select All
          </button>
          <button class="btn btn-deselect" @click="deselectAll">
            Deselect All
          </button>
          <button
            class="btn btn-apply"
            :disabled="selectedLeaseIds.length === 0 || applying"
            @click="applyLateFees"
          >
            <i class="fas fa-dollar-sign"></i>
            Apply to {{ selectedLeaseIds.length }} selected
          </button>
        </div>
      </div>

      <!-- Summary -->
      <div class="summary-bar">
        <span
          >{{ sortedProperties.length
          }}<span v-if="searchQuery"> of {{ lateProperties.length }}</span>
          properties</span
        >
        <span v-if="lastAppliedMessage" class="applied-message">
          <i class="fas fa-check-circle"></i> {{ lastAppliedMessage }}
        </span>
      </div>

      <!-- Loading state -->
      <div v-if="loading" class="loading-state">
        <i class="fas fa-spinner fa-spin"></i> Loading late payment data...
      </div>

      <!-- Table -->
      <div v-else class="table-container">
        <table class="late-fees-table">
          <thead>
            <tr>
              <th class="col-check">
                <input
                  type="checkbox"
                  :checked="allSelected"
                  @change="toggleAll"
                />
              </th>
              <th class="col-address">Address</th>
              <th class="col-tenants">Tenants</th>
              <th class="col-rent">Rent</th>
              <th
                class="col-last-payment sortable-header"
                @click="toggleSort('lastPayment')"
              >
                Last Paid
                <i
                  :class="
                    sortColumn === 'lastPayment'
                      ? sortDirection === 'asc'
                        ? 'fas fa-sort-up'
                        : 'fas fa-sort-down'
                      : 'fas fa-sort'
                  "
                  :style="{
                    opacity: sortColumn === 'lastPayment' ? 1 : 0.3,
                    marginLeft: '3px',
                  }"
                ></i>
              </th>
              <th
                class="col-balance sortable-header"
                @click="toggleSort('balance')"
              >
                Balance
                <i
                  :class="
                    sortColumn === 'balance'
                      ? sortDirection === 'asc'
                        ? 'fas fa-sort-up'
                        : 'fas fa-sort-down'
                      : 'fas fa-sort'
                  "
                  :style="{
                    opacity: sortColumn === 'balance' ? 1 : 0.3,
                    marginLeft: '3px',
                  }"
                ></i>
              </th>
              <th class="col-lstat">LStat</th>
              <th class="col-status">Status</th>
            </tr>
          </thead>
          <tbody>
            <tr
              v-for="row in sortedProperties"
              :key="row.leaseId"
              :class="{
                selected: selectedLeaseIds.includes(row.leaseId),
                'has-late-fee': row.hasLateFeeThisMonth,
              }"
              @click="!row.hasLateFeeThisMonth && toggleRow(row.leaseId)"
            >
              <td class="col-check">
                <input
                  v-if="!row.hasLateFeeThisMonth"
                  type="checkbox"
                  :checked="selectedLeaseIds.includes(row.leaseId)"
                  @click.stop
                  @change="toggleRow(row.leaseId)"
                />
              </td>
              <td
                class="col-address address-link"
                @dblclick.stop="openLeaseSearch(row)"
              >
                {{ row.address }}
              </td>
              <td
                class="col-tenants"
                :title="
                  row.hasLateFeeThisMonth
                    ? 'Late fee already applied this month'
                    : ''
                "
              >
                {{ row.tenants || '—' }}
              </td>
              <td class="col-rent">${{ formatMoney(row.monthlyBaseRent) }}</td>
              <td class="col-last-payment">
                {{ row.lastPaymentDate || 'Never' }}
              </td>
              <td
                class="col-balance"
                :class="{
                  'balance-negative': parseFloat(row.balance) < 0,
                  'balance-positive': parseFloat(row.balance) > 0,
                }"
              >
                ${{ formatMoney(row.balance) }}
              </td>
              <td
                class="col-lstat"
                :title="
                  row.leaseStateDescription
                    ? row.leaseStateDescription +
                      (row.leaseStateDate ? '\n' + row.leaseStateDate : '')
                    : ''
                "
                :style="{
                  color: row.leaseStateForeground || undefined,
                  backgroundColor: row.leaseStateBackground || undefined,
                }"
              >
                {{ row.leaseState }}
              </td>
              <td class="col-status">
                <span class="status-badge" :class="statusClass(row.status)">{{
                  row.status
                }}</span>
              </td>
            </tr>
          </tbody>
        </table>
      </div>
    </div>

    <!-- Confirm Dialog -->
    <div v-if="showConfirmDialog" class="confirm-overlay">
      <div class="confirm-dialog" :class="{ 'confirm-error': confirmError }">
        <div class="confirm-icon">
          <i
            :class="
              confirmError
                ? 'fas fa-exclamation-triangle'
                : 'fas fa-file-invoice-dollar'
            "
          ></i>
        </div>
        <div class="confirm-message">{{ confirmMessage }}</div>
        <div class="confirm-actions" v-if="!confirmError">
          <button
            class="confirm-btn confirm-cancel"
            @click="showConfirmDialog = false"
          >
            Cancel
          </button>
          <button class="confirm-btn confirm-ok" @click="confirmApply">
            Apply Fees
          </button>
        </div>
        <div class="confirm-actions" v-else>
          <button
            class="confirm-btn confirm-cancel"
            @click="showConfirmDialog = false"
          >
            Close
          </button>
        </div>
      </div>
    </div>

    <!-- Lease Search iframe overlay -->
    <div v-if="iframeUrl" class="iframe-overlay" @click.self="iframeUrl = null">
      <div class="iframe-container">
        <div class="iframe-title-bar">
          <span>{{ iframeTitle }}</span>
          <button class="iframe-close-btn" @click="iframeUrl = null">
            <i class="fas fa-times"></i>
          </button>
        </div>
        <div class="iframe-scroll-wrapper">
          <div v-if="iframeLoading" class="iframe-spinner-overlay">
            <i class="fas fa-spinner fa-spin"></i>
            <span>Loading lease…</span>
          </div>
          <iframe
            :src="iframeUrl"
            class="lease-iframe"
            @load="iframeLoading = false"
          ></iframe>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
import axios from '@/axiosConfig';

export default {
  name: 'LateFees',

  data() {
    return {
      lateProperties: [],
      selectedLeaseIds: [],
      searchQuery: '',
      feeAmount: 40,
      loading: false,
      applying: false,
      lastAppliedMessage: '',
      iframeUrl: null,
      iframeTitle: '',
      iframeLoading: true,
      showConfirmDialog: false,
      confirmMessage: '',
      confirmError: false,
      sortColumn: 'balance',
      sortDirection: 'asc',
    };
  },

  computed: {
    selectableProperties() {
      return this.lateProperties.filter((r) => !r.hasLateFeeThisMonth);
    },
    allSelected() {
      return (
        this.selectableProperties.length > 0 &&
        this.selectedLeaseIds.length === this.selectableProperties.length
      );
    },
    filteredProperties() {
      if (!this.searchQuery.trim()) return this.lateProperties;
      const q = this.searchQuery.toLowerCase().trim();
      return this.lateProperties.filter(
        (r) =>
          (r.address || '').toLowerCase().includes(q) ||
          (r.tenants || '').toLowerCase().includes(q) ||
          (r.status || '').toLowerCase().includes(q) ||
          (r.leaseState || '').toLowerCase().includes(q) ||
          (r.leaseStateDescription || '').toLowerCase().includes(q),
      );
    },
    sortedProperties() {
      const dir = this.sortDirection === 'asc' ? 1 : -1;
      return [...this.filteredProperties].sort((a, b) => {
        if (this.sortColumn === 'balance') {
          return (parseFloat(a.balance) - parseFloat(b.balance)) * dir;
        }
        // lastPayment
        const dateA = a.lastPaymentRaw
          ? new Date(a.lastPaymentRaw)
          : new Date(0);
        const dateB = b.lastPaymentRaw
          ? new Date(b.lastPaymentRaw)
          : new Date(0);
        return (dateA - dateB) * dir;
      });
    },
  },

  mounted() {
    this.fetchLateFees();
  },

  methods: {
    async fetchLateFees() {
      this.loading = true;
      this.lastAppliedMessage = '';
      try {
        const response = await axios.get('/api/late-fees');
        this.lateProperties = response.data;
      } catch (error) {
        console.error('Error fetching late fees data:', error);
      } finally {
        this.loading = false;
      }
    },

    toggleRow(leaseId) {
      const idx = this.selectedLeaseIds.indexOf(leaseId);
      if (idx === -1) {
        this.selectedLeaseIds.push(leaseId);
      } else {
        this.selectedLeaseIds.splice(idx, 1);
      }
    },

    toggleAll() {
      if (this.allSelected) {
        this.selectedLeaseIds = [];
      } else {
        this.selectedLeaseIds = this.lateProperties
          .filter((r) => !r.hasLateFeeThisMonth)
          .map((r) => r.leaseId);
      }
    },

    selectAll() {
      this.selectedLeaseIds = this.lateProperties
        .filter((r) => !r.hasLateFeeThisMonth)
        .map((r) => r.leaseId);
    },

    deselectAll() {
      this.selectedLeaseIds = [];
    },

    toggleSort(column) {
      if (this.sortColumn === column) {
        this.sortDirection = this.sortDirection === 'asc' ? 'desc' : 'asc';
      } else {
        this.sortColumn = column;
        this.sortDirection = 'asc';
      }
    },

    async applyLateFees() {
      if (this.selectedLeaseIds.length === 0 || this.feeAmount <= 0) return;

      const count = this.selectedLeaseIds.length;
      this.confirmMessage = `Apply a $${this.feeAmount.toFixed(2)} late fee to ${count} lease(s)?`;
      this.confirmError = false;
      this.showConfirmDialog = true;
    },

    async confirmApply() {
      this.showConfirmDialog = false;
      this.applying = true;
      try {
        const response = await axios.post('/api/late-fees/apply', {
          leaseIds: this.selectedLeaseIds,
          amount: this.feeAmount,
          description: 'Late Fee',
        });
        this.lastAppliedMessage = response.data.message;
        this.selectedLeaseIds = [];
        // Refresh the data
        await this.fetchLateFees();
      } catch (error) {
        console.error('Error applying late fees:', error);
        this.confirmMessage =
          'Failed to apply late fees. Check console for details.';
        this.confirmError = true;
        this.showConfirmDialog = true;
      } finally {
        this.applying = false;
      }
    },

    openLeaseSearch(row) {
      const resolved = this.$router.resolve({
        path: '/lease-search',
        query: { search: row.address.replace('#', ''), hideNav: '1' },
      });
      this.iframeTitle = row.address;
      this.iframeLoading = true;
      this.iframeUrl = resolved.href;
    },

    formatMoney(val) {
      const num = parseFloat(val) || 0;
      return num.toLocaleString('en-US', {
        minimumFractionDigits: 2,
        maximumFractionDigits: 2,
      });
    },

    statusClass(status) {
      if (status === 'No payments') return 'status-danger';
      if (status === 'No payment this month') return 'status-warning';
      if (status === 'Paid late') return 'status-late';
      if (status === 'Paid early') return 'status-early';
      if (status === 'On time') return 'status-ontime';
      return '';
    },
  },
};
</script>

<style scoped>
.late-fees-panel {
  width: 100%;
  height: 100%;
  display: flex;
  flex-direction: column;
  background: #f5f5f5;
  border-radius: 5px;
  overflow: hidden;
}

.late-fees-title {
  font-size: 1.2em;
  background: #6e5875;
  color: #eee;
  padding: 10px 15px;
  display: flex;
  justify-content: space-between;
  align-items: center;
}

.late-fees-title .close-btn {
  background: #907793;
  border: 1px solid #e1dede;
  color: #eee;
  border-radius: 50%;
  width: 28px;
  height: 28px;
  cursor: pointer;
  display: flex;
  align-items: center;
  justify-content: center;
  font-size: 0.7em;
}

.late-fees-title .close-btn:hover {
  opacity: 0.8;
}

.title-actions {
  display: flex;
  align-items: center;
  gap: 6px;
}

.late-fees-body {
  flex: 1;
  display: flex;
  flex-direction: column;
  padding: 10px 15px;
  overflow: hidden;
}

.controls-row {
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-bottom: 10px;
  gap: 15px;
  flex-wrap: wrap;
}

.fee-input-group {
  display: flex;
  align-items: center;
  gap: 5px;
  font-size: 0.85em;
}

.fee-input {
  width: 80px;
  padding: 4px 6px;
  border: 1px solid #aaa;
  border-radius: 4px;
  font-size: 1em;
}

.control-buttons {
  display: flex;
  gap: 6px;
}

.btn {
  padding: 5px 12px;
  border: none;
  border-radius: 4px;
  cursor: pointer;
  font-size: 0.78em;
  color: white;
}

.btn-select-all {
  background: #6c757d;
}

.btn-deselect {
  background: #6c757d;
}

.btn-apply {
  background: #a080a8;
}

.btn-apply:disabled {
  background: #ccc;
  cursor: not-allowed;
}

/* Confirm Dialog */
.confirm-overlay {
  position: fixed;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  background: rgba(0, 0, 0, 0.5);
  display: flex;
  align-items: center;
  justify-content: center;
  z-index: 40000;
}

.confirm-dialog {
  background: #2a2a3a;
  border-radius: 10px;
  padding: 28px 32px 22px;
  min-width: 340px;
  max-width: 420px;
  box-shadow: 0 8px 32px rgba(0, 0, 0, 0.5);
  text-align: center;
  color: #eee;
}

.confirm-dialog.confirm-error {
  border: 1px solid #e57373;
}

.confirm-icon {
  font-size: 2.2em;
  margin-bottom: 14px;
  color: #a080a8;
}

.confirm-error .confirm-icon {
  color: #e57373;
}

.confirm-message {
  font-size: 1.05em;
  margin-bottom: 22px;
  line-height: 1.5;
}

.confirm-actions {
  display: flex;
  gap: 12px;
  justify-content: center;
}

.confirm-btn {
  padding: 8px 22px;
  border: none;
  border-radius: 5px;
  cursor: pointer;
  font-size: 0.9em;
  font-weight: 600;
  transition: opacity 0.15s;
}

.confirm-btn:hover {
  opacity: 0.85;
}

.confirm-cancel {
  background: #555;
  color: #eee;
}

.confirm-ok {
  background: #a080a8;
  color: #fff;
}

.btn:hover:not(:disabled) {
  opacity: 0.85;
}

.summary-bar {
  font-size: 0.8em;
  color: #555;
  margin-bottom: 8px;
  display: flex;
  justify-content: space-between;
  align-items: center;
}

.applied-message {
  color: #4caf50;
  font-weight: 500;
}

.loading-state {
  text-align: center;
  padding: 40px;
  color: #888;
  font-size: 0.9em;
}

.table-container {
  flex: 1;
  overflow-y: auto;
  border: 1px solid #ddd;
  border-radius: 4px;
  scrollbar-width: none; /* Firefox */
  -ms-overflow-style: none; /* IE and Edge */
}

.table-container::-webkit-scrollbar {
  width: 0px;
  background: transparent; /* Chrome, Safari, Opera */
}

.late-fees-table {
  width: 100%;
  border-collapse: collapse;
  font-size: 0.78em;
}

.late-fees-table thead {
  position: sticky;
  top: 0;
  z-index: 1;
}

.late-fees-table th {
  background: #333;
  color: #eee;
  padding: 6px 8px;
  text-align: left;
  font-weight: 600;
  white-space: nowrap;
}

.late-fees-table td {
  padding: 5px 8px;
  border-bottom: 1px solid #e0e0e0;
}

.late-fees-table tbody tr:nth-child(even) {
  background: #f9f9f9;
}

.late-fees-table tbody tr:nth-child(odd) {
  background: #fff;
}

.late-fees-table tbody tr:hover {
  background: #e8e0ec;
  cursor: pointer;
}

.late-fees-table tbody tr.selected {
  background: #d4c5da;
}

.col-check {
  width: 30px;
  text-align: center;
}

.col-address {
  min-width: 160px;
}

.col-tenants {
  min-width: 140px;
}

.sortable-header {
  cursor: pointer;
  user-select: none;
}

th.col-rent,
th.col-balance,
th.col-last-payment,
.col-rent,
.col-balance,
.col-last-payment {
  white-space: nowrap;
  text-align: right;
}

.late-fees-table .col-status {
  text-align: center;
}

.col-lstat {
  text-align: center;
  min-width: 40px;
  max-width: 50px;
  font-weight: 600;
  font-size: 0.95em;
}

.has-late-fee {
  opacity: 0.55;
  cursor: default !important;
}

.balance-negative {
  background: #fde8e8;
  color: #c62828;
  font-weight: 600;
}

.balance-positive {
  background: #e8f5e9;
  color: #2e7d32;
  font-weight: 600;
}

.status-badge {
  display: inline-block;
  padding: 2px 8px;
  border-radius: 10px;
  font-size: 0.9em;
  font-weight: 500;
  white-space: nowrap;
}

.status-danger {
  background: #f44336;
  color: white;
}

.status-warning {
  background: #ff9800;
  color: white;
}

.status-late {
  background: #ffc107;
  color: #333;
}

.status-early {
  background: #4caf50;
  color: white;
}

.status-ontime {
  background: #2196f3;
  color: white;
}

.search-input-group {
  position: relative;
  display: flex;
  align-items: center;
  flex: 1;
  max-width: 350px;
}

.search-icon {
  position: absolute;
  left: 8px;
  color: #999;
  font-size: 0.8em;
  pointer-events: none;
}

.search-input {
  width: 100%;
  padding: 5px 28px 5px 28px;
  border: 1px solid #aaa;
  border-radius: 4px;
  font-size: 0.85em;
}

.search-input::placeholder {
  color: #aaa;
  font-size: 0.95em;
}

.search-clear-btn {
  position: absolute;
  right: 4px;
  background: none;
  border: none;
  color: #999;
  cursor: pointer;
  font-size: 0.75em;
  padding: 2px 4px;
}

.search-clear-btn:hover {
  color: #333;
}

.address-link {
  cursor: pointer;
  color: #4a2d5e;
  text-decoration: underline;
  text-decoration-style: dotted;
}

.address-link:hover {
  color: #a080a8;
}

.iframe-overlay {
  position: fixed;
  top: 0;
  left: 0;
  width: 100vw;
  height: 100vh;
  background: rgba(0, 0, 0, 0.55);
  z-index: 30000;
  display: flex;
  justify-content: center;
  align-items: flex-start;
}

.iframe-container {
  width: 90vw;
  max-width: 1400px;
  height: 100vh;
  background: #fff;
  overflow: hidden;
  box-shadow: 0 8px 30px rgba(0, 0, 0, 0.4);
  display: flex;
  flex-direction: column;
}

.iframe-title-bar {
  display: flex;
  justify-content: space-between;
  align-items: center;
  padding: 8px 14px;
  background: #333;
  color: #eee;
  font-size: 0.95em;
  flex-shrink: 0;
}

.iframe-close-btn {
  background: #555;
  border: 1px solid #777;
  color: #eee;
  border-radius: 50%;
  width: 26px;
  height: 26px;
  cursor: pointer;
  display: flex;
  align-items: center;
  justify-content: center;
  font-size: 0.7em;
}

.iframe-close-btn:hover {
  background: #d32f2f;
}

.iframe-scroll-wrapper {
  flex: 1;
  overflow: hidden;
  position: relative;
}

.iframe-spinner-overlay {
  position: absolute;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  background: #1e1e2e;
  z-index: 1;
  gap: 12px;
  color: #ccc;
  font-size: 1.1em;
}

.iframe-spinner-overlay .fa-spinner {
  font-size: 2em;
  color: #4fc3f7;
}

.lease-iframe {
  border: none;
  width: 100%;
  height: 100%;
}
</style>
